import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EntityFormPage } from "@/components/entity-page/EntityFormPage";
import EditIntegrationTypeForm from "@/components/EditIntegrationTypeForm";
import { getIntegrationTypes } from "@/lib/formOptions";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/integration-types/$id/")({
  component: EditIntegrationTypePage,
});

function EditIntegrationTypePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [integrationType, setIntegrationType] = useState<FormOption | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    let active = true;
    getIntegrationTypes()
      .then((options) => {
        if (!active) return;
        const found = options.find((option) => option.id === id);
        if (!found) {
          setNotFound(true);
        } else {
          setIntegrationType(found);
        }
        setLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Error fetching integration type:", error);
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const goToList = () => navigate({ to: "/dashboard/integration-types" });

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !integrationType) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Integration type not found</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <EntityFormPage
      title="Edit Integration Type"
      description={`Update ${integrationType.label}'s catalog listing.`}
      backHref="/dashboard/integration-types"
      headerActions={
        !showDeleteConfirm ? (
          <Button type="button" variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        ) : undefined
      }
    >
      <EditIntegrationTypeForm
        integrationType={integrationType}
        showDeleteConfirm={showDeleteConfirm}
        onCancelDelete={() => setShowDeleteConfirm(false)}
        onDeleted={goToList}
        onSaved={goToList}
      />
    </EntityFormPage>
  );
}
